import React, { Fragment, useState, useRef, useEffect, useCallback } from 'react';

function useHeights(){
  const heightsRef = useRef({});
  const [heightKey,setHeightKey] = useState(0);

  return [heightsRef,heightKey,setHeightKey];
}

function useSliderState({numItems,interval=2000,autoplay=false}){
  const [index,setIndexState] = useState(0);
  const [lastIndex,setLastIndex] = useState(numItems-1);
  const [playing,setPlaying] = useState(autoplay);

  const setIndex = useCallback(
    newIndex => {
      if(newIndex !== index){
        setLastIndex(index);
        setIndexState(newIndex);
      }
    },[
      index,
      setLastIndex,
      setIndexState
    ]
  );

  const next = useCallback(
    (stopPlaying=true)=>{
      if(stopPlaying)setPlaying(false);
      if(index + 1 < numItems){
        setIndex(index + 1);
      }else{
        setIndex(0);
      }
    },[
      index,
      setIndex,
      numItems,
    ]
  );

  const prev = useCallback(
    (stopPlaying=true)=>{
      if(stopPlaying)setPlaying(false);
      if(index - 1 > -1){
        setIndex(index - 1);
      }else{
        setIndex(numItems - 1);
      }
    },[
      index,
      setIndex,
      numItems,
    ]
  );

  useEffect(
    ()=>{
      if(!playing || !interval) return;
      let mounted = true;

      const intervalHandle = setInterval(() => {
        if(!mounted) return;
        next(false);
      },interval);

      return ()=>{
        clearInterval(intervalHandle);
        mounted = false;
      }
    },[
      interval,
      playing,
      next
    ]
  );

  return {index,setIndex,lastIndex,next,prev,playing,setPlaying};
}

function Slide({indexOffset,src,title,alt,description,active,wasActive,onHeight}){

  const elementRef = useRef();
  const callbackRef = useRef(onHeight);

  useEffect(()=>{
    callbackRef.current = onHeight;
  },[onHeight]);

  useEffect(
    ()=>{
      const onResize=()=>{
        const {current:callback} = callbackRef;
        callback&&callback(elementRef.current.getBoundingClientRect().height);
      }
      window.addEventListener('resize',onResize);
      onResize();
      return ()=>{
        window.removeEventListener('resize',onResize);
      }
    },
    []
  );

  const [stateClass,setStateClass] = useState('idle');

  useEffect(
    ()=>{
      const newStateClass = active ? 'in' : (wasActive ? 'out' : 'idle');

      setStateClass(newStateClass);

      if(newStateClass !== 'out') return;

      let mounted = true;

      const timeout = setTimeout(
        ()=>{
          mounted&&setStateClass('idle');
        },
        1000
      );

      return ()=>{
        mounted = false;
        clearTimeout(timeout);
      }
    },[
      setStateClass,stateClass,active,wasActive
    ]
  );


  return (
    <div ref={elementRef} style={{'--index-offset':indexOffset}} className={`${stateClass} polaroid-slider__slide`}>
      <div className="columns is-vcentered">
        <div className="column is-4 is-offset-1">
          <figure className="image is-5by4 is-cover image-layout-6">
            <img src={src} alt={alt}/>
            <figcaption>
              {title}
            </figcaption>
          </figure>
        </div>
        <div className="column description is-offset-1" dangerouslySetInnerHTML={{__html:description}} />
      </div>
    </div>
  )
}

export default function PolaroidSlider({items,title,interval=5000,rootElement}){
  const {index,next,prev,lastIndex,setPlaying} = useSliderState({numItems:items.length, autoplay:false, interval});
  const [heightsRef,heightKey,setHeightKey] = useHeights();

  useEffect(
    ()=>{
      let mounted = true;

      const handleChange = e=>{
        const section = e.detail.elements[e.detail.currentIndex];
        if(section.contains(rootElement)){
          setPlaying(true);
        }
      }
      document.addEventListener('sections:change',handleChange);

      return ()=>{
        mounted = false;
        document.removeEventListener('sections:change',handleChange);
      }
    }
    ,[setPlaying,rootElement]
  );

  return (
    <Fragment>
      {title && (<div dangerouslySetInnerHTML={{__html:title}} />)}
      <div style={{'--index':index,'--length':items.length,'--height':Object.values(heightsRef.current).reduce((p,v)=>p<v?v:p,0)+'px'}} className="polaroid-slider__slide-container">
        {items.map((item,key)=>(
          <Slide
            {...item}
            onHeight={height => {
              setHeightKey(heightKey+1);
              heightsRef.current[`item${key}`] = height;
            }}
            key={key}
            active={key === index}
            wasActive={key === lastIndex}
            indexOffset={key - index}
            />
          ))}
      </div>
      <div className={`controls`}>
        <button onClick={prev}>{'←'}</button>
        {index+1}{' / '}{items.length}
        <button onClick={next}>{'→'}</button>
      </div>
      {/* <div className={`progress ${playing ? 'playing' : ''}`} style={{'--interval':`${interval}ms0`}}></div> */}
    </Fragment>
  );
}